import { useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  BellRing,
  CheckCircle2,
  Clock3,
  ExternalLink,
  KeyRound,
  LoaderCircle,
  RefreshCw,
  Settings,
  ShieldCheck,
  Trash2,
} from "lucide-react";
import {
  CopyableDate,
  EmptyState,
  PageHeader,
  useRouter,
} from "@tribunalea/ui";
import { useApi } from "../App.jsx";

const PJM_NOTIFICATIONS_LIST_URL =
  "https://notificaciones.jus.mendoza.gov.ar/notificaciones/list";

function formatDate(value, fallback = "Todavía no sincronizada") {
  if (!value) return fallback;
  return new Intl.DateTimeFormat("es-AR", {
    timeZone: "America/Argentina/Mendoza",
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

function CredentialsForm({
  currentUsername = "",
  saving,
  onCancel,
  onSubmit,
}) {
  const [username, setUsername] = useState(currentUsername);
  const [password, setPassword] = useState("");
  return (
    <form
      className="integration-config-form"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit({ username, password });
      }}
    >
      <div className="integration-config-heading">
        <span><KeyRound size={20} /></span>
        <div>
          <h2>Credenciales del PJM</h2>
          <p>
            Usaremos estos datos únicamente para consultar tus notificaciones.
          </p>
        </div>
      </div>
      <label>
        <span>Usuario</span>
        <input
          autoComplete="username"
          value={username}
          onChange={(event) => setUsername(event.target.value)}
          required
        />
      </label>
      <label>
        <span>Contraseña</span>
        <input
          autoComplete="current-password"
          type="password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          required
        />
      </label>
      <div className="integration-credential-note">
        <ShieldCheck size={17} />
        <span>La contraseña se guarda cifrada y nunca se muestra nuevamente.</span>
      </div>
      <div className="integration-form-actions">
        {onCancel && (
          <button className="secondary-button" type="button" onClick={onCancel}>
            Cancelar
          </button>
        )}
        <button className="primary-button" disabled={saving}>
          {saving && <LoaderCircle className="spin" size={17} />}
          {saving ? "Conectando y sincronizando…" : "Guardar y conectar"}
        </button>
      </div>
    </form>
  );
}

export function PjmNotificationsIntegrationPage() {
  const api = useApi();
  const { navigate } = useRouter();
  const [integration, setIntegration] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const load = async () => {
    const result = await api.getPjmNotificationsIntegration();
    setIntegration(result.integration);
    setNotifications(result.notifications);
  };

  useEffect(() => {
    let active = true;
    api.getPjmNotificationsIntegration()
      .then((result) => {
        if (!active) return;
        setIntegration(result.integration);
        setNotifications(result.notifications);
      })
      .catch((requestError) => {
        if (active) setError(requestError.message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [api]);

  const orderedNotifications = useMemo(
    () => [...notifications].sort((left, right) =>
      String(right.occurredAt || "").localeCompare(
        String(left.occurredAt || ""),
      )),
    [notifications],
  );

  const saveCredentials = async (credentials) => {
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const result = await api.configurePjmNotificationsIntegration(
        credentials,
      );
      await load();
      window.dispatchEvent(new Event("praxis:integrations-changed"));
      setEditing(false);
      setMessage(
        `Integración conectada. Incorporamos ${result.sync.inserted} notificaciones nuevas.`,
      );
    } catch (requestError) {
      setError(requestError.message);
      await load().catch(() => {});
    } finally {
      setSaving(false);
    }
  };

  const syncNow = async () => {
    setSyncing(true);
    setError("");
    setMessage("");
    try {
      const result = await api.syncPjmNotificationsIntegration();
      await load();
      setMessage(
        result.inserted
          ? `Incorporamos ${result.inserted} notificaciones nuevas.`
          : "La integración ya estaba al día.",
      );
    } catch (requestError) {
      setError(requestError.message);
      await load().catch(() => {});
    } finally {
      setSyncing(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(
      "¿Querés quitar la integración y borrar sus notificaciones guardadas?",
    )) return;
    setRemoving(true);
    setError("");
    try {
      await api.removePjmNotificationsIntegration();
      setIntegration(null);
      setNotifications([]);
      window.dispatchEvent(new Event("praxis:integrations-changed"));
      setEditing(false);
      setMessage("La integración fue eliminada.");
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setRemoving(false);
    }
  };

  if (loading) {
    return (
      <div className="page integration-detail-page">
        <div className="integrations-loading">
          <LoaderCircle className="spin" size={24} />
          <span>Abriendo integración…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="page integration-detail-page">
      <button
        className="integration-back"
        type="button"
        onClick={() => navigate("/integraciones")}
      >
        <ArrowLeft size={15} />
        Integraciones
      </button>
      <PageHeader
        eyebrow="Integraciones"
        title="Sistema de Notificaciones del PJM"
        description="Consultá desde Praxis las notificaciones recibidas en el Poder Judicial de Mendoza."
      />

      {error && <div className="integration-feedback is-error">{error}</div>}
      {message && (
        <div className="integration-feedback is-success">
          <CheckCircle2 size={16} />
          {message}
        </div>
      )}

      {!integration || editing ? (
        <CredentialsForm
          currentUsername={integration?.username}
          saving={saving}
          onCancel={integration ? () => setEditing(false) : null}
          onSubmit={saveCredentials}
        />
      ) : (
        <>
          <section className="integration-status-card">
            <div className="integration-status-main">
              <span className="integration-status-icon"><BellRing size={23} /></span>
              <div>
                <span className={`integration-state is-configured ${
                  integration.status === "error" ? "has-error" : ""
                }`}>
                  {integration.status === "error" ? "Requiere atención" : "Conectada"}
                </span>
                <strong>Usuario {integration.username}</strong>
                <small>
                  <Clock3 size={13} />
                  Última sincronización:{" "}
                  <CopyableDate value={integration.lastSyncedAt}>
                    {formatDate(integration.lastSyncedAt)}
                  </CopyableDate>
                </small>
              </div>
            </div>
            <div className="integration-status-actions">
              <button
                className="secondary-button"
                type="button"
                onClick={() => setEditing(true)}
              >
                <Settings size={16} />
                Configurar
              </button>
              <button
                className="primary-button"
                type="button"
                disabled={syncing}
                onClick={syncNow}
              >
                <RefreshCw className={syncing ? "spin" : ""} size={16} />
                {syncing ? "Sincronizando…" : "Revisar ahora"}
              </button>
              <button
                className="integration-remove-button"
                type="button"
                disabled={removing}
                onClick={remove}
                aria-label="Quitar integración"
              >
                <Trash2 size={16} />
              </button>
            </div>
            {integration.lastSyncError && (
              <p className="integration-sync-error">
                {integration.lastSyncError}
              </p>
            )}
          </section>

          <aside className="integration-alerts-note">
            <span><BellRing size={18} /></span>
            <div>
              <strong>Recibí avisos de nuevas notificaciones</strong>
              <p>
                Podés configurar la recepción de alertas desde la sección
                Alertas.
              </p>
            </div>
            <button type="button" onClick={() => navigate("/alertas")}>
              Ir a Alertas
              <ArrowRight size={15} />
            </button>
          </aside>

          <section className="pjm-notifications-card">
            <header>
              <div>
                <span className="eyebrow">Notificaciones recibidas</span>
                <h2>Actividad reciente</h2>
              </div>
              <span>{orderedNotifications.length} guardadas</span>
            </header>
            {orderedNotifications.length ? (
              <div className="pjm-notifications-list">
                {orderedNotifications.map((notification) => (
                  <article
                    className={
                      notification.isRead
                        ? "is-read"
                        : "is-unread"
                    }
                    key={notification.id}
                  >
                    <span className="pjm-notification-icon">
                      <BellRing size={17} />
                    </span>
                    <div className="pjm-notification-copy">
                      <div className="pjm-notification-meta">
                        <time>
                          <CopyableDate value={notification.occurredAt}>
                            {formatDate(
                              notification.occurredAt,
                              "Fecha no informada",
                            )}
                          </CopyableDate>
                        </time>
                        <span
                          className={
                            notification.isRead
                              ? "pjm-read-status is-read"
                              : "pjm-read-status is-unread"
                          }
                        >
                          {notification.isRead ? "Leída" : "No leída"}
                        </span>
                        {notification.status && (
                          <span>{notification.status}</span>
                        )}
                      </div>
                      <h3>{notification.title || "Notificación del PJM"}</h3>
                      {notification.body && <p>{notification.body}</p>}
                      <div className="pjm-notification-tags">
                        {notification.expediente && (
                          <span>Expediente {notification.expediente}</span>
                        )}
                        {notification.foja && (
                          <span>Foja {notification.foja}</span>
                        )}
                        {notification.organismo && (
                          <span>{notification.organismo}</span>
                        )}
                      </div>
                    </div>
                    <a
                      className="pjm-notification-view"
                      href={PJM_NOTIFICATIONS_LIST_URL}
                      target="_blank"
                      rel="noreferrer"
                      aria-label="Ver notificaciones en el PJM"
                    >
                      Ver
                      <ExternalLink size={15} />
                    </a>
                  </article>
                ))}
              </div>
            ) : (
              <EmptyState
                icon={BellRing}
                title="Todavía no hay notificaciones"
                description="Cuando el PJM publique nuevas notificaciones, las verás en este espacio."
              />
            )}
          </section>
        </>
      )}
    </div>
  );
}
